import { ReactElement } from 'react';
import { useNavigate, useHref } from 'react-router-dom';

import { useNavigation } from '../../main';

import { RouterLink, RouterLinkProps } from './RouterLink';
import { StyledLink } from './StyledLink';

export interface LinkProps extends Omit<RouterLinkProps, 'navigationHook' | 'originalTo'> {
  /** Specifies what the routing will be performed relative to.
   * Examples below with service path 'test' and root url 'scp':
   *
   *-default: use href props of link
   *
   *-global: use platform instance of react-router-dom. Redirect to '/' will result 'scp';
   *
   *-inner: use current instance of react-router-dom. Redirect to '/' will result  'scp/test'.
   */
  navigationType?: 'inner' | 'global' | 'default';
  /**
   * Shortcut for navigationType='inner'
   */
  inner?: boolean;
}

/**
 * @description Link component
 */
export function Link({ navigationType = 'default', to, inner, ...rest }: LinkProps): ReactElement {
  const fixedTo = useHref(to ?? '');
  if (inner) {
    navigationType = 'inner';
  }
  if (navigationType === 'default') return <StyledLink to={to} {...rest} />;

  const navigationHook = navigationType === 'inner' ? useNavigate : useNavigation;

  return (
    <RouterLink
      to={navigationType === 'inner' ? fixedTo : to}
      originalTo={to ?? ''}
      {...rest}
      navigationHook={navigationHook}
    />
  );
}
